/**
 * Catálogo de temas (paletas) — solo desarrollo local.
 *
 * Cada tema define los valores de las variables CSS que ya usa la app.
 * El selector aplica los valores con `document.documentElement.style.setProperty`
 * y persiste la elección en localStorage. El default es `abyss` (el
 * tema baseline de la suite Alpha+).
 *
 * Los colores semánticos (success / warning / danger / info) y las
 * tipografías quedan constantes — solo varían superficies, texto, bordes
 * y los acentos de marca para que la UI mantenga legibilidad y semántica.
 */

export type ThemeMode = 'dark' | 'light';

export interface ThemePalette {
  id: string;
  label: string;
  mode: ThemeMode;
  /** Color del swatch en el selector (se ve como circulito de marca). */
  swatch: string;
  vars: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Helpers para mantener la definición compacta
// ---------------------------------------------------------------------------

interface MiniPalette {
  base: string;       // surface-base (más oscuro / claro)
  s1: string;         // surface-1
  s2: string;         // surface-2
  s3: string;         // surface-3
  canvas: string;     // surface-canvas
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textDisabled: string;
  borderSubtle: string;
  borderDefault: string;
  borderStrong: string;
  accent: string;
  accentSoft: string;
  brandAlt: string;
  brandViolet: string;
}

function buildTheme(
  id: string, label: string, mode: ThemeMode, swatch: string, p: MiniPalette,
): ThemePalette {
  return {
    id, label, mode, swatch,
    vars: {
      '--surface-base': p.base,
      '--surface-1': p.s1,
      '--surface-2': p.s2,
      '--surface-3': p.s3,
      '--surface-canvas': p.canvas,
      '--text-primary': p.textPrimary,
      '--text-secondary': p.textSecondary,
      '--text-muted': p.textMuted,
      '--text-disabled': p.textDisabled,
      '--border-subtle': p.borderSubtle,
      '--border-default': p.borderDefault,
      '--border-strong': p.borderStrong,
      '--accent': p.accent,
      '--accent-soft': p.accentSoft,
      '--brand-alt': p.brandAlt,
      '--brand-violet': p.brandViolet,
      'color-scheme': mode,
    },
  };
}

// ---------------------------------------------------------------------------
// 14 temas
// ---------------------------------------------------------------------------

export const THEMES: ThemePalette[] = [
  // ── Dark ──────────────────────────────────────────────────────────────
  buildTheme('abyss', 'Abyss', 'dark', '#f59e0b', {
    base: '#020617', s1: '#0f172a', s2: '#1e293b', s3: '#334155',
    canvas: '#0b1220',
    textPrimary: '#f8fafc', textSecondary: '#cbd5e1',
    textMuted: '#94a3b8', textDisabled: '#64748b',
    borderSubtle: 'rgba(148,163,184,0.10)',
    borderDefault: 'rgba(148,163,184,0.18)',
    borderStrong: 'rgba(148,163,184,0.30)',
    accent: '#f59e0b', accentSoft: '#fcd34d',
    brandAlt: '#3b82f6', brandViolet: '#8b5cf6',
  }),
  buildTheme('night', 'Night', 'dark', '#a78bfa', {
    base: '#0c0a1a', s1: '#171432', s2: '#1f1b40', s3: '#2d2856',
    canvas: '#0a0817',
    textPrimary: '#f4f1ff', textSecondary: '#c9c2e8',
    textMuted: '#8c84b8', textDisabled: '#5b5483',
    borderSubtle: 'rgba(167,139,250,0.10)',
    borderDefault: 'rgba(167,139,250,0.20)',
    borderStrong: 'rgba(167,139,250,0.32)',
    accent: '#a78bfa', accentSoft: '#c4b5fd',
    brandAlt: '#60a5fa', brandViolet: '#c084fc',
  }),
  buildTheme('dim', 'Dim', 'dark', '#94a3b8', {
    base: '#1c1917', s1: '#292524', s2: '#3a3633', s3: '#4d4844',
    canvas: '#23201d',
    textPrimary: '#fafaf9', textSecondary: '#d6d3d1',
    textMuted: '#a8a29e', textDisabled: '#78716c',
    borderSubtle: 'rgba(168,162,158,0.10)',
    borderDefault: 'rgba(168,162,158,0.20)',
    borderStrong: 'rgba(168,162,158,0.32)',
    accent: '#fbbf24', accentSoft: '#fde68a',
    brandAlt: '#60a5fa', brandViolet: '#a78bfa',
  }),
  buildTheme('nord', 'Nord', 'dark', '#88c0d0', {
    base: '#2e3440', s1: '#3b4252', s2: '#434c5e', s3: '#4c566a',
    canvas: '#252a33',
    textPrimary: '#eceff4', textSecondary: '#d8dee9',
    textMuted: '#9aa3b3', textDisabled: '#646e80',
    borderSubtle: 'rgba(216,222,233,0.10)',
    borderDefault: 'rgba(216,222,233,0.18)',
    borderStrong: 'rgba(216,222,233,0.30)',
    accent: '#88c0d0', accentSoft: '#8fbcbb',
    brandAlt: '#81a1c1', brandViolet: '#b48ead',
  }),
  buildTheme('coffee', 'Coffee', 'dark', '#d4a373', {
    base: '#1a120b', s1: '#2b1f15', s2: '#3a2b1f', s3: '#4d3a2a',
    canvas: '#1f170f',
    textPrimary: '#fef3e2', textSecondary: '#e8d5b7',
    textMuted: '#a89881', textDisabled: '#6e6353',
    borderSubtle: 'rgba(232,213,183,0.10)',
    borderDefault: 'rgba(232,213,183,0.18)',
    borderStrong: 'rgba(232,213,183,0.32)',
    accent: '#d4a373', accentSoft: '#e9c895',
    brandAlt: '#7fb069', brandViolet: '#bc8a5f',
  }),
  buildTheme('caramellatte', 'Caramellatte', 'dark', '#deb887', {
    base: '#231a13', s1: '#33271c', s2: '#473625', s3: '#5b4630',
    canvas: '#2a1f17',
    textPrimary: '#faf0e0', textSecondary: '#dec5a3',
    textMuted: '#a89070', textDisabled: '#6e5d48',
    borderSubtle: 'rgba(222,184,135,0.10)',
    borderDefault: 'rgba(222,184,135,0.20)',
    borderStrong: 'rgba(222,184,135,0.32)',
    accent: '#deb887', accentSoft: '#f4d4a8',
    brandAlt: '#c08552', brandViolet: '#9d6f3e',
  }),
  buildTheme('sunset', 'Sunset', 'dark', '#fb7185', {
    base: '#1a0a1e', s1: '#2b1233', s2: '#3d1948', s3: '#572561',
    canvas: '#1e0c23',
    textPrimary: '#ffe4ef', textSecondary: '#f0b6d4',
    textMuted: '#b48598', textDisabled: '#7a5263',
    borderSubtle: 'rgba(251,113,133,0.10)',
    borderDefault: 'rgba(251,113,133,0.20)',
    borderStrong: 'rgba(251,113,133,0.32)',
    accent: '#fb7185', accentSoft: '#fda4af',
    brandAlt: '#f97316', brandViolet: '#c084fc',
  }),
  buildTheme('acid', 'Acid', 'dark', '#a3e635', {
    base: '#0a0f04', s1: '#13190a', s2: '#1d2410', s3: '#2f3a1a',
    canvas: '#0d1305',
    textPrimary: '#ecfccb', textSecondary: '#bef264',
    textMuted: '#84a55e', textDisabled: '#566b3f',
    borderSubtle: 'rgba(163,230,53,0.10)',
    borderDefault: 'rgba(163,230,53,0.22)',
    borderStrong: 'rgba(163,230,53,0.40)',
    accent: '#a3e635', accentSoft: '#d9f99d',
    brandAlt: '#22d3ee', brandViolet: '#e879f9',
  }),
  buildTheme('business', 'Business', 'dark', '#60a5fa', {
    base: '#0a0e1a', s1: '#141a2b', s2: '#1f273e', s3: '#2d3756',
    canvas: '#0d121f',
    textPrimary: '#e5e7eb', textSecondary: '#9ca3af',
    textMuted: '#6b7280', textDisabled: '#4b5563',
    borderSubtle: 'rgba(156,163,175,0.08)',
    borderDefault: 'rgba(156,163,175,0.16)',
    borderStrong: 'rgba(156,163,175,0.28)',
    accent: '#60a5fa', accentSoft: '#93c5fd',
    brandAlt: '#34d399', brandViolet: '#a78bfa',
  }),

  // ── Light ─────────────────────────────────────────────────────────────
  buildTheme('autumn', 'Autumn', 'light', '#ea580c', {
    base: '#fef3e7', s1: '#fff7ed', s2: '#fde9d4', s3: '#fbd2ad',
    canvas: '#fef9f3',
    textPrimary: '#1c1410', textSecondary: '#5c3a1f',
    textMuted: '#92633c', textDisabled: '#bf926a',
    borderSubtle: 'rgba(154,93,33,0.10)',
    borderDefault: 'rgba(154,93,33,0.20)',
    borderStrong: 'rgba(154,93,33,0.35)',
    accent: '#ea580c', accentSoft: '#fb923c',
    brandAlt: '#0891b2', brandViolet: '#7c3aed',
  }),
  buildTheme('cmyk', 'CMYK', 'light', '#06b6d4', {
    base: '#fafafa', s1: '#ffffff', s2: '#f5f5f5', s3: '#e8e8e8',
    canvas: '#fbfbfb',
    textPrimary: '#171717', textSecondary: '#404040',
    textMuted: '#737373', textDisabled: '#a3a3a3',
    borderSubtle: 'rgba(82,82,82,0.10)',
    borderDefault: 'rgba(82,82,82,0.18)',
    borderStrong: 'rgba(82,82,82,0.30)',
    accent: '#06b6d4', accentSoft: '#67e8f9',
    brandAlt: '#d946ef', brandViolet: '#facc15',
  }),
  buildTheme('winter', 'Winter', 'light', '#0ea5e9', {
    base: '#f0f9ff', s1: '#ffffff', s2: '#e0f2fe', s3: '#bae6fd',
    canvas: '#f7fbfd',
    textPrimary: '#0c1e2e', textSecondary: '#1e3a52',
    textMuted: '#506782', textDisabled: '#94a8bf',
    borderSubtle: 'rgba(14,165,233,0.10)',
    borderDefault: 'rgba(14,165,233,0.20)',
    borderStrong: 'rgba(14,165,233,0.35)',
    accent: '#0ea5e9', accentSoft: '#7dd3fc',
    brandAlt: '#6366f1', brandViolet: '#a855f7',
  }),
  buildTheme('silk', 'Silk', 'light', '#a78bfa', {
    base: '#fafaf9', s1: '#ffffff', s2: '#f5f5f4', s3: '#e7e5e4',
    canvas: '#faf9f7',
    textPrimary: '#1c1917', textSecondary: '#44403c',
    textMuted: '#78716c', textDisabled: '#a8a29e',
    borderSubtle: 'rgba(120,113,108,0.10)',
    borderDefault: 'rgba(120,113,108,0.18)',
    borderStrong: 'rgba(120,113,108,0.30)',
    accent: '#a78bfa', accentSoft: '#c4b5fd',
    brandAlt: '#f472b6', brandViolet: '#7c3aed',
  }),
  buildTheme('lemonade', 'Lemonade', 'light', '#facc15', {
    base: '#fefce8', s1: '#ffffff', s2: '#fef9c3', s3: '#fef08a',
    canvas: '#fefdf1',
    textPrimary: '#1a1a05', textSecondary: '#3f3a1f',
    textMuted: '#736b3a', textDisabled: '#a89e58',
    borderSubtle: 'rgba(133,77,14,0.10)',
    borderDefault: 'rgba(133,77,14,0.18)',
    borderStrong: 'rgba(133,77,14,0.30)',
    accent: '#facc15', accentSoft: '#fde047',
    brandAlt: '#84cc16', brandViolet: '#f97316',
  }),
];

export const DEFAULT_THEME_ID = 'abyss';

export function getThemeById(id: string): ThemePalette {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

/** Aplica un tema al :root del documento. */
export function applyTheme(theme: ThemePalette): void {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(theme.vars)) {
    if (k.startsWith('--')) {
      root.style.setProperty(k, v);
    }
  }
  root.style.colorScheme = theme.mode;
  root.setAttribute('data-theme', theme.id);
}
